<i18n>
  {
    "en": {
      "networks": {
        "type": "Type",
        "broadcastAddress": "Broadcast",
        "netmaskAddress": "Netmask",
        "netmaskMask": "Mask",
        "startAddress": "IP From",
        "endAddress": "IP To",
        "startPort": "Port From",
        "endPort": "Port To",
        "agentUrl": "Agent URL",
        "location": "Location"
      }
    },
    "ru": {
      "networks": {
        "type": "Тип",
        "broadcastAddress": "Широковещательный IP",
        "netmaskAddress": "Адрес",
        "netmaskMask": "Маска",
        "startAddress": "IP Начальный",
        "endAddress": "IP Конечный",
        "startPort": "Начальный порт",
        "endPort": "Конечный порт",
        "agentUrl": "URL агента",
        "location": "Локация"
      }
    }
  }
</i18n>

<template>
  <div id="networks">
    <div class="app-buttons">
      <dx-button type="normal"
        :text="$t('general.resetFilter')"
        styling-mode="contained"
        @click="onResetState"
      />
    </div>
  
    <dx-data-grid
      class="data-grid dx-card"
      :data-source="items"
      :height="gridHeight"
      :remote-operations="remoteOperations"
      :syncLookupFilterValues="false"
      @row-click="onRowClick"
      @context-menu-preparing="addMenuItems"
      @editor-preparing="onEditorPreparing"
      @content-ready="onContentReady"
      @initialized=initializedGrid
      ref="dataGrid"
    >
      <dx-paging :enabled="true" />
      <dx-pager
        :visible="true"
        :allowed-page-sizes="pageSize"
        display-mode="full"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="false"
      />
      <dx-scrolling mode="standard" />
      <dx-filter-panel :visible="showFilterPanel" />
      <dx-filter-row :visible="true" />
      <dx-sorting mode="multiple" />
      <dx-grouping :auto-expand-all="true" />
      <dx-state-storing
        :enabled="true"
        type="custom"
        :customLoad="stateLoad"
        :customSave="stateSave"
      />

      <dx-column
        data-field="location.name"
        :caption="$t('networks.location')"
        :width="160"
        :visible-index="0"
      />
      <dx-column
        data-field="agent.url"
        :caption="$t('networks.agentUrl')"
        :visible-index="1"
      />
      <dx-column
        data-field="rangeType"
        :caption="$t('networks.type')"
        :width="120"
        :visible-index="2"
      />
      <dx-column
        data-field="broadcastAddress"
        :caption="$t('networks.broadcastAddress')"
        :width="120"
        :visible-index="3"
      />
      <dx-column
        data-field="netmaskAddress"
        :caption="$t('networks.netmaskAddress')"
        :width="120"
        :visible-index="4"
      />
      <dx-column
        data-field="netmaskMask"
        :caption="$t('networks.netmaskMask')"
        :width="120"
        :visible-index="5"
      />
      <dx-column
        data-field="startAddress"
        :caption="$t('networks.startAddress')"
        :width="120"
        :visible-index="6"
      />
      <dx-column
        data-field="endAddress"
        :caption="$t('networks.endAddress')"
        :width="120"
        :visible-index="7"
      />
      <dx-column
        data-field="startPort"
        :caption="$t('networks.startPort')"
        :width="80"
        :visible-index="8"
      />
      <dx-column
        data-field="endPort"
        :caption="$t('networks.endPort')"
        :width="80"
        :visible-index="9"
      />
    </dx-data-grid>
  
    <CustomColumnChooser
      :visible="visible"
      :columns="columns"
      :onApply="onApply"
      :onHiding="onHiding"
    />
  </div>
</template>
  
<script>
import baseView from "../base";

import DxDataGrid, {
  DxColumn, DxFilterRow, DxPaging, DxStateStoring, DxPager, DxSorting, DxFilterPanel, DxScrolling, DxGrouping
} from "devextreme-vue/data-grid";
import { DxButton } from 'devextreme-vue';
import CustomStore from 'devextreme/data/custom_store';
  
import CustomColumnChooser from "@/components/column-chooser/CustomColumnChooser.vue";

import { getLocationAgents, findNetworkRanges } from "@/services/api";
import { AclHandler } from "@/services/acl-handler";

import { DataLocation } from "@/mixins/dataLocationMixin";
import { DataDrid } from "@/mixins/dataDridMixin";

import {
  PAGE_HEADER_HEIGHT,
  PAGE_PADDING,
  PAGE_TOOLBARS_HEIGHT,
  PAGE_FOOTER_HEIGHT,
  GRID_HEADER_HEIGHT,
  GRID_FILTER_ROW_HEIGHT,
  GRID_FILTER_PANEL_HEIGHT,
  GRID_PAGER_HEIGHT
} from '@/shared/consts/layout';

export default {
  name: 'locationNetworks',
  props: {
    locationId: {
      type: Number,
      required: true
    },
    dataSource: {
      type: Object,
      required: false
    },
    locationsItems: {
      type: Array,
      required: false
    },
    agentsItems: {
      type: Array,
      required: false
    },
    locationDictionaryChild: {
      type: Array,
      required: false
    },
    statisticsDevices: {
      type: Object,
      required: false
    }
  },
  extends: baseView,
  mixins: [DataDrid, DataLocation],
  components: {
    DxDataGrid, DxColumn, DxFilterRow, DxPaging, DxButton, DxStateStoring, CustomColumnChooser,
    DxPager, DxSorting, DxFilterPanel, DxScrolling, DxGrouping
  },
  data() {
    return {
      isCloudMode: this.$store.getters.isCloudMode,
      remoteOperations: { groupPaging: true },
      items: new CustomStore({
        key: 'id',                        
        load : async (options) => {
          const take = options.take || this.countRows(this.gridBodyHeight);
          const skip = options.skip || 0;
          const pageNumber = Math.floor(skip / take) + 1;
          const page = [take, pageNumber].join(",");

          const sort = Array.isArray(options.sort)
            ? options.sort.map(s => `${s.selector},${s.desc ? "DESC" : "ASC"}`)
            : [];

          const agents = await getLocationAgents(this.locationId);
          const agentIds = await agents.map((item) => item.id);

          const baseFilter = ['agent.id', 'anyof', agentIds];

          let filterJson = baseFilter;

          if (options.filter) {
            filterJson = [baseFilter, 'and', options.filter];
          }
          const query = { page, sort, filterJson };

          const response = await findNetworkRanges(query);

          return { totalCount : response.totalRows, data : response.pageRows };
        },
      })
    };
  },
  computed: {
    appUser() {
      return this.$store.getters.user;
    },
    aclUser() {
      return new AclHandler(this.appUser, this.isCloudMode);
    },
    showFilterPanel() {
      return this.aclUser.hasGrantForOperation('BTN_CLEAR_FILTER', 'EXECUTE') || false;
    },
    sumGap() {
      const padding = 80;
      const tabsHeight = 37;

      return padding + PAGE_HEADER_HEIGHT + PAGE_PADDING + PAGE_TOOLBARS_HEIGHT + tabsHeight + PAGE_FOOTER_HEIGHT;
    },
    gridHeight() {
      const appHeightSize = this.$store.getters.appContainerSize.height;
      return (appHeightSize || 800) - this.sumGap;
    },
    gridBodyHeight() {
      return this.gridHeight - GRID_HEADER_HEIGHT - GRID_FILTER_ROW_HEIGHT - (this.showFilterPanel ? GRID_FILTER_PANEL_HEIGHT : 0) - GRID_PAGER_HEIGHT;
    },
  },
  methods: {
    onRowClick(e) {
      if (window.getSelection().focusOffset !== window.getSelection().anchorOffset || !e.data.id)
        return;

      if (e.event.metaKey || e.event.ctrlKey) {
        const routeData = this.$router.resolve({
          name: "network",
          params: { id: e.data.id }
        });
        window.open(routeData.href, "_blank");
      } else {
        this.$router.push({
          name: "network",
          params: { id: e.data.id }
        });
      }
    },
    onEditorPreparing(e) {
      this.applyFilterRowTimeout(e);
    },
    onContentReady(e) {
      this.addFitToPageHeightButton(e);
    },
    onResetState() {
      this.dataGrid.clearFilter();
    },
  },
  created() {
    this.gridStateKey = 'gridLocationNetworks';
  },
  beforeDestroy() {
    const state = this.dataGrid.state();
    this.stateSave(state);
  }
};
</script>
  
<style lang="scss">
</style>
  